// AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
// Soorten afspraak (verzoek BuurtKlusBedrijf 2026-10-06): de titel van een
// afspraak uit een lijst kiezen in plaats van typen. In productie is de titel
// bij élke organisatie in feite de soort bezoek ('1e Huisbezoek', 'Opname',
// 'FIX', 'J1h'), in zes schrijfwijzen. Een vaste lijst per organisatie maakt
// er één van, en 'Anders…' houdt de vrije tekst open.
//
// Opslag: `CompagnyMeta.AppointmentKinds` is een map per sleutel
// `{ Label, Hidden, Position }`, net als AdressLabels, zodat een veld met een
// dotted path te schrijven is. De afspraak draagt de gekozen naam als `Title`
// (tekst, zoals altijd) én de sleutel als `Kind`; hernoemen raakt dus geen
// oude afspraak, en tellen per soort kan op de sleutel.

import { v4 as uuid } from 'uuid'

/**
 * Ingebouwd voor het FIXtool-merk: de soorten die de FIXtool-organisaties al
 * met de hand typten, plus de drie 'wel werk, geen klus' van BuurtKlusBedrijf.
 * Een organisatie kan ze hernoemen en verbergen, niet weghalen.
 * @type {Array<{key: string, label: string}>}
 */
const FIXTOOL_KINDS = [
    { key: 'huisbezoek', label: 'Huisbezoek' },
    { key: 'opname', label: 'Opname' },
    { key: 'fixen', label: 'Fixen' },
    { key: 'coach', label: 'Coach' },
    { key: 'cursus', label: 'Cursus' },
    { key: 'intern', label: 'Intern' },
    { key: 'gereserveerd', label: 'Gereserveerd' },
]

/** De titel die een huisbezoek krijgt als de organisatie geen soorten heeft. */
const FALLBACK_VISIT_TITLE = 'Huisbezoek'

/**
 * Aan/uit-vlaggen komen in twee vormen voor: ToggleButton schrijft tekst.
 * @param {*} value
 * @returns {boolean}
 */
const aan = (value) => value === true || value === 'true'

/**
 * Waarvoor de uren van een soort tellen in de SiSa/SPUK-verantwoording
 * (besluit 8 in docs/features/huishouden-vaste-gegevens.md). Leeg = telt niet.
 * Geen standaard per ingebouwde soort: wat 'Huisbezoek' is, verschilt per
 * organisatie, dus dat kiest de organisatie zelf.
 * @type {Array<{value: string, label: string}>}
 */
const INZET_OPTIONS = [
    { value: '', label: 'Telt niet' },
    { value: 'uitvoering', label: 'Uitvoering' },
    { value: 'advies', label: 'Advies' },
]

/**
 * @param {*} value - `AppointmentKinds[key].Inzet` zoals opgeslagen
 * @returns {'uitvoering'|'advies'|''}
 */
const inzetOf = (value) => (value === 'uitvoering' || value === 'advies' ? value : '')

/**
 * @typedef {object} AppointmentKind
 * @property {string} key - De sleutel zoals hij op de afspraak komt (`Kind`)
 * @property {string} label - De naam die de gebruiker leest en die `Title` wordt
 * @property {boolean} builtIn - Ingebouwd (sleutel ligt vast) of eigen
 * @property {'uitvoering'|'advies'|''} inzet - Waarvoor de uren tellen; leeg = niet
 * @property {boolean} [hidden] - Alleen met includeHidden
 */

/**
 * De soorten van een organisatie, zichtbaar en op volgorde: eerst de
 * ingebouwde van het merk, dan de eigen op `Position`. De bovenste is de
 * standaard voor een nieuw huisbezoek.
 *
 * @param {object} [meta] - Het CompagnyMeta-document (of alleen `AppointmentKinds`)
 * @param {object} [opties]
 * @param {string} [opties.brand] - Merknaam uit de Hostname-hook ('' zolang hij laadt); alleen 'FIXtool' heeft ingebouwde soorten
 * @param {boolean} [opties.includeHidden=false] - Ook de verborgen soorten, met `hidden: true` (instellingenscherm)
 * @returns {Array<AppointmentKind>}
 */
const resolveAppointmentKinds = (meta, { brand = '', includeHidden = false } = {}) => {
    const config = (meta && meta.AppointmentKinds && typeof meta.AppointmentKinds === 'object' && !Array.isArray(meta.AppointmentKinds))
        ? meta.AppointmentKinds
        : {}

    const naam = (eigen, standaard) => {
        const tekst = typeof eigen === 'string' ? eigen.trim() : ''
        return tekst || standaard
    }

    const ingebouwdeLijst = brand === 'FIXtool' ? FIXTOOL_KINDS : []
    const ingebouwd = ingebouwdeLijst
        .filter((k) => includeHidden || !aan(config[k.key]?.Hidden))
        .map((k) => ({
            key: k.key,
            label: naam(config[k.key]?.Label, k.label),
            builtIn: true,
            inzet: inzetOf(config[k.key]?.Inzet),
            ...(includeHidden ? { hidden: aan(config[k.key]?.Hidden) } : {}),
        }))

    const eigen = Object.keys(config)
        .filter((key) => !ingebouwdeLijst.some((k) => k.key === key) && !FIXTOOL_KINDS.some((k) => k.key === key))
        .map((key) => ({ key, ...config[key] }))
        .filter((k) => (includeHidden || !aan(k.Hidden)) && typeof k.Label === 'string' && k.Label.trim() !== '')
        .sort((a, b) => (Number(a.Position) || 0) - (Number(b.Position) || 0))
        .map((k) => ({
            key: k.key,
            label: k.Label.trim(),
            builtIn: false,
            inzet: inzetOf(k.Inzet),
            ...(includeHidden ? { hidden: aan(k.Hidden) } : {}),
        }))

    return [...ingebouwd, ...eigen]
}

/**
 * De titel en soort voor een nieuw huisbezoek: de bovenste soort van de
 * organisatie, en zonder soorten 'Huisbezoek' zoals altijd.
 * @param {Array<AppointmentKind>} kinds
 * @returns {{Title: string, Kind: string}}
 */
const defaultVisitTitle = (kinds) => (
    Array.isArray(kinds) && kinds.length > 0
        ? { Title: kinds[0].label, Kind: kinds[0].key }
        : { Title: FALLBACK_VISIT_TITLE, Kind: '' }
)

/**
 * Welke soort bij een titel hoort — zodat een bestaande afspraak in de
 * keuzelijst op zijn soort staat en niet op 'Anders…'. Leeg = geen soort.
 * @param {Array<AppointmentKind>} kinds
 * @param {string} [title]
 * @returns {string}
 */
const kindKeyForTitle = (kinds, title) => {
    const gezocht = String(title || '').trim().toLowerCase()
    if (!gezocht) return ''
    const match = (kinds || []).find((k) => k.label.trim().toLowerCase() === gezocht)
    return match ? match.key : ''
}

/** @returns {string} Een sleutel voor een eigen soort van de organisatie */
const newCustomKindKey = () => `kind-${uuid()}`

export { FIXTOOL_KINDS, FALLBACK_VISIT_TITLE, INZET_OPTIONS, defaultVisitTitle, kindKeyForTitle, newCustomKindKey }
export default resolveAppointmentKinds
